import { Globe, Heart, MessageCircle, MoreHorizontal, Play, Send, Smile } from "lucide-react";

/**
 * The ad picture (design: components/marketing/AdPoster.tsx).
 *
 * The design file prints its sample clinic ("Sunrise Dental", its address and
 * phone) on every poster. Here they are props: `brand` is the clinic's name
 * and `contact` the line under the hero shot; both empty when unknown.
 */

const PALETTES = [
  { panel: "bg-sun text-ink", chip: "bg-ink text-paper", button: "bg-ink text-paper", photo: "bg-sky-soft", mark: "text-sky-deep" },
  { panel: "bg-ink text-paper", chip: "bg-sun text-ink", button: "bg-sun text-ink", photo: "bg-mist", mark: "text-ink" },
  { panel: "bg-sky-soft text-ink", chip: "bg-paper text-ink", button: "bg-ink text-paper", photo: "bg-sun-soft", mark: "text-ink" },
];

/** Stand-in for the photograph until the clinic adds one. Drawn, so it never looks like a broken image. */
function Photo({ photo, tone, markTone }) {
  if (photo) return <img src={photo} alt="" className="h-full w-full object-cover" />;
  return (
    <div className={`relative flex h-full w-full items-center justify-center overflow-hidden ${tone}`}>
      <span className="absolute h-[140%] w-[140%] rounded-full border-[0.6em] border-paper/50" />
      <span className="absolute h-[95%] w-[95%] rounded-full border-[0.6em] border-paper/60" />
      <span className="absolute h-[55%] w-[55%] rounded-full bg-paper/70" />
      <Smile className={`relative h-[32%] w-[32%] ${markTone}`} strokeWidth={1.5} />
    </div>
  );
}

function Brand({ light, brand }) {
  if (!brand) return <span />;
  return (
    <span className={`flex items-center gap-[0.35em] text-[0.62em] font-semibold ${light ? "text-paper" : ""}`}>
      <span className={`flex h-[1.5em] w-[1.5em] items-center justify-center rounded-full text-[0.8em] ${light ? "bg-paper text-ink" : "bg-ink text-paper"}`}>{brand.trim().charAt(0).toUpperCase()}</span>
      {brand}
    </span>
  );
}

const offerOf = (benefits) => benefits.find((b) => /₹|%|\d/.test(b)) ?? benefits[0] ?? "Book today";

/** The ad picture itself. `ratio` switches between the square feed poster and the 9:16 story. Sized by `width` in px. */
export function AdPoster({ style, variant, headline, benefits = [], cta, photo, brand = "", contact = "", ratio = "square", width = 320 }) {
  const pal = PALETTES[variant % PALETTES.length];
  const story = ratio === "story";
  const box = { width, fontSize: width / 15 };
  const aspect = story ? "aspect-[9/16]" : "aspect-square";

  if (style === "Claim card") {
    const offer = offerOf(benefits);
    return (
      <div style={box} className={`${aspect} flex flex-col justify-between overflow-hidden p-[1em] ${pal.panel}`}>
        <Brand light={variant === 1} brand={brand} />
        <div>
          <p className="text-[2.6em] leading-[0.95] font-semibold tracking-tight tabular-nums">{offer}</p>
          <p className="mt-[0.4em] text-[0.95em] leading-tight font-semibold">{headline}</p>
        </div>
        <span className={`w-fit rounded-full px-[0.8em] py-[0.35em] text-[0.6em] font-semibold ${pal.button}`}>{cta}</span>
      </div>
    );
  }

  if (style === "Dark and premium") {
    return (
      <div style={box} className={`${aspect} relative flex flex-col overflow-hidden bg-ink text-paper`}>
        <div className="relative min-h-0 flex-1 opacity-80"><Photo photo={photo} tone="bg-muted" markTone="text-paper" /></div>
        <div className="flex flex-col gap-[0.45em] p-[0.9em]">
          <span className="h-[0.12em] w-[2.4em] bg-sun" />
          <p className="text-[1.15em] leading-tight font-semibold">{headline}</p>
          <div className="flex items-center justify-between">
            <span className="text-[0.55em] text-paper/70">{benefits.join("  ·  ")}</span>
            <Brand light brand={brand} />
          </div>
        </div>
      </div>
    );
  }

  if (style === "Hero shot") {
    return (
      <div style={box} className={`${aspect} relative overflow-hidden`}>
        <Photo photo={photo} tone={pal.photo} markTone={pal.mark} />
        <div className={`absolute inset-x-0 top-0 p-[0.8em] ${pal.panel}`}>
          <p className="text-[1.05em] leading-tight font-semibold">{headline}</p>
        </div>
        <div className="absolute inset-x-0 bottom-0 flex items-center justify-between bg-paper/90 px-[0.8em] py-[0.5em] text-ink">
          <Brand brand={brand} />
          <span className="text-[0.5em] text-muted">{contact}</span>
        </div>
      </div>
    );
  }

  // Split panel
  return (
    <div style={box} className={`${aspect} flex overflow-hidden ${story ? "flex-col" : "flex-row"}`}>
      <div className={story ? "h-1/2 w-full" : "h-full w-1/2"}><Photo photo={photo} tone={pal.photo} markTone={pal.mark} /></div>
      <div className={`flex flex-1 flex-col justify-between p-[0.8em] ${pal.panel}`}>
        <Brand light={variant === 1} brand={brand} />
        <div className="flex flex-col gap-[0.45em]">
          <p className="text-[1.15em] leading-[1.05] font-semibold">{headline}</p>
          <div className="flex flex-wrap gap-[0.3em]">
            {benefits.map((b) => <span key={b} className={`rounded-full px-[0.55em] py-[0.2em] text-[0.5em] font-semibold ${pal.chip}`}>{b}</span>)}
          </div>
        </div>
        <span className={`w-fit rounded-full px-[0.7em] py-[0.35em] text-[0.52em] font-semibold ${pal.button}`}>{cta}</span>
      </div>
    </div>
  );
}

/* ---------- Previews ----------
 * The design prints its sample clinic (name, Instagram handle, website) in
 * these frames. Here they come from the draft: `brand`, `handle`, `site`.
 * `image` is a generated or uploaded ad picture; when there is one it is
 * shown as it is, because the server already printed the words on it.
 */

const Picture = ({ draft, ratio, width }) => (draft.image
  ? <img src={draft.image} alt="Ad" style={{ width }} className={`${ratio === "story" ? "aspect-[9/16]" : "aspect-square"} block object-cover`} />
  : <AdPoster {...draft} ratio={ratio} width={width} />);
const initial = (s = "") => s.trim().charAt(0).toUpperCase();

/** How the ad looks in someone's Facebook or Instagram feed, or as a story. */
export function AdPreview({ draft, surface }) {
  if (surface === "Story") {
    return (
      <div className="relative mx-auto w-[260px] overflow-hidden rounded-lg bg-ink shadow-lift">
        <Picture draft={draft} ratio="story" width={260} />
        <div className="absolute inset-x-3 top-3 flex items-center gap-2 text-paper">
          <span className="h-0.5 flex-1 rounded-full bg-paper/80" />
        </div>
        <div className="absolute inset-x-3 top-6 flex items-center gap-2 text-caption text-paper">
          <span className="flex h-6 w-6 items-center justify-center rounded-full bg-paper text-caption font-semibold text-ink">{initial(draft.brand)}</span>
          <span className="font-semibold">{draft.handle || draft.brand}</span>
          <span className="text-paper/70">Sponsored</span>
        </div>
        <div className="absolute inset-x-0 bottom-0 flex justify-center pb-4">
          <span className="rounded-full bg-paper px-4 py-2 text-caption font-semibold text-ink shadow-soft">{draft.cta}</span>
        </div>
      </div>
    );
  }
  return (
    <div className="mx-auto w-[320px] overflow-hidden rounded-lg bg-paper shadow-lift ring-1 ring-line">
      <div className="flex items-center gap-2 px-3 py-2.5">
        <span className="flex h-8 w-8 items-center justify-center rounded-full bg-ink text-caption font-semibold text-paper">{initial(draft.brand)}</span>
        <div className="min-w-0 flex-1">
          <p className="text-small font-semibold">{draft.brand}</p>
          <p className="flex items-center gap-1 text-caption text-muted">Sponsored · <Globe size={11} strokeWidth={1.75} /></p>
        </div>
        <MoreHorizontal size={16} strokeWidth={1.75} className="text-muted" />
      </div>
      <p className="px-3 pb-2.5 text-small leading-snug">{draft.primary}</p>
      <Picture draft={draft} width={320} />
      <div className="flex items-center gap-3 bg-mist px-3 py-2.5">
        <div className="min-w-0 flex-1">
          <p className="text-caption text-muted">{draft.site}</p>
          <p className="truncate text-small font-semibold">{draft.headline}</p>
          <p className="truncate text-caption text-muted">{draft.description}</p>
        </div>
        <span className="shrink-0 rounded-sm bg-line px-3 py-2 text-caption font-semibold">{draft.cta}</span>
      </div>
      <div className="flex items-center gap-4 px-3 py-2.5 text-muted">
        <Heart size={17} strokeWidth={1.75} /><MessageCircle size={17} strokeWidth={1.75} /><Send size={17} strokeWidth={1.75} />
      </div>
    </div>
  );
}

export function PlainPhotoPreview({ draft, width = 320 }) {
  const photo = draft.image || draft.photo;
  return <div className="mx-auto overflow-hidden rounded-lg bg-paper shadow-lift ring-1 ring-line" style={{ width }}>
    <div className="aspect-square bg-mist">{photo ? <img src={photo} alt="Campaign" className="h-full w-full object-cover" /> : <div className="flex h-full w-full items-center justify-center bg-[radial-gradient(circle_at_35%_35%,var(--color-sun-soft),var(--color-sky-soft)_52%,var(--color-mist))]"><span className="h-28 w-28 rounded-full border-[12px] border-paper/70" /></div>}</div>
    <div className="p-3"><p className="text-small font-semibold">{draft.headline}</p><p className="text-caption text-muted">{[draft.cta, draft.brand].filter(Boolean).join(" · ")}</p></div>
  </div>;
}

export function VideoPreview({ draft }) {
  const presenterInitial = draft.presenter?.startsWith("Dr.") ? (draft.presenter.split(" ")[1] || "").charAt(0) : initial(draft.presenter) || "O";
  return <div className="relative mx-auto w-[250px] overflow-hidden rounded-lg bg-ink shadow-lift ring-1 ring-line">
    <div className="relative aspect-[9/16] overflow-hidden bg-mist">
      {/* The made clip once there is one (item 39); the design's mock until then. */}
      {draft.videoUrl ? <video src={draft.videoUrl} className="absolute inset-0 h-full w-full object-cover" controls playsInline muted loop /> : <AdPoster {...draft} ratio="story" width={250} />}
      {!draft.videoUrl && draft.videoType === "Doctor avatar" && <div className="absolute left-1/2 top-[28%] flex h-20 w-20 -translate-x-1/2 items-center justify-center rounded-full bg-paper text-h2 font-semibold text-ink shadow-soft ring-4 ring-paper/70">{presenterInitial}</div>}
      {!draft.videoUrl && <span className="absolute left-1/2 top-1/2 flex h-14 w-14 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full bg-paper/90 text-ink shadow-soft"><Play size={22} fill="currentColor" strokeWidth={1.75} /></span>}
      <span className="absolute right-3 top-3 rounded-full bg-ink/80 px-2.5 py-1 text-caption font-semibold text-paper">{draft.videoLength}s</span>
      {!draft.videoUrl && <span className="absolute inset-x-3 bottom-5 rounded-xs bg-ink/75 px-2 py-1.5 text-center text-caption font-medium text-paper">{draft.videoScript || [draft.service, draft.headline].filter(Boolean).join(": ")}</span>}
    </div>
  </div>;
}
